import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  confirmForgotPassword,
  forgotPassword,
} from "../../services/auth/auth";

import { Mail, Lock, CheckCheck, Eye, EyeOff } from "lucide-react";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import logo from "../../assets/logo-cloud.png";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [flag, setFlag] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const handleConfirmForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setFormError("");
    setSuccess(false);
    if (!code.trim() || !newPassword.trim()) {
      setFormError("Please enter the confirmation code");
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(email, code, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      const errorMessage =
        error.message || "Failed to Reset Password. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setError("");
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    setLoading(true);
    try {
      await forgotPassword(email);
      setFlag(true);
    } catch (error) {
      const errorMessage = "Enter valid email";
      setError(errorMessage);
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#003366",
        fontFamily: `"Source Sans Pro", "Amazon Ember", "Raleway", sans-serif`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          boxShadow: "inset 0 0 50px rgba(0, 0, 0, 0.5)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        component="img"
        src={logo}
        alt="Logo"
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          width: "100px",
        }}
      />
      <Paper
        sx={{
          width: "400px",
          padding: "30px 40px",
          borderRadius: "8px",
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: "#003366",
            fontWeight: 600,
            fontFamily: `"Raleway", sans-serif`,
          }}
        >
          <CheckCircle sx={{ color: "#003366", marginRight: 1 }} />
          Reset Password
        </Typography>
        <form>
          <TextField
            id="email"
            label="Email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Email"
            InputProps={{
              readOnly: flag,
              startAdornment: (
                <InputAdornment position="start">
                  <Mail className="h-6 w-6 text-[#003366]" />
                </InputAdornment>
              ),
            }}
            required
            fullWidth
            margin="normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#003366",
                },
              },
              backgroundColor: "#F0F8FF",
              borderRadius: 1,
            }}
          />

          {flag ? (
            <>
              <TextField
                id="Confirmation"
                label="Confirmation Code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-label="Confirmation Code"
                required
                fullWidth
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CheckCheck className="h-6 w-6 text-[#003366]" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#003366",
                    },
                  },
                  backgroundColor: "#F0F8FF",
                  borderRadius: 1,
                }}
              />
              <TextField
                id="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                aria-label="Password"
                required
                fullWidth
                margin="normal"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="h-6 w-6 text-[#003366]" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-[#003366]" />
                          ) : (
                            <Eye className="h-4 w-4 text-[#003366]" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#003366",
                    },
                  },
                  backgroundColor: "#F0F8FF",
                  borderRadius: 1,
                }}
              />
            </>
          ) : (
            <></>
          )}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#FFD700",
                color: "#003366",
                padding: "10px 16px",
                borderRadius: "6px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#FFC300",
                },
              }}
              onClick={flag ? handleConfirmForgotPassword : handleOtp}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  {flag ? (
                    <Typography
                      sx={{
                        color: "#003366",
                        fontFamily: ` "Raleway", sans-serif`,
                      }}
                    >
                      Reset
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        color: "#003366",
                        fontFamily: ` "Raleway", sans-serif`,
                      }}
                    >
                      Get Verification Code
                    </Typography>
                  )}
                </>
              )}
            </Button>
          </Box>
        </form>
        <Box sx={{ mt: 2 }}>
          <Link to="/login">
            <Typography
              sx={{
                marginTop: "20px",
                color: "#003366",
                fontFamily: ` "Raleway", sans-serif`,
              }}
            >
              Remember the Password? Log in
            </Typography>
          </Link>
        </Box>
      </Paper>
      {/* Success Message */}
      <Snackbar
        open={success}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Password Reset successfully! Redirecting to login...
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPassword;
